import classNames from 'classnames';
import React, {useMemo} from 'react';

import {ContextualHeader} from '@udemy/react-browse-contextual-header';
import {CertificationModel} from '@udemy/react-certification-preparation';
import {AlternateHeadline, SkeletonTitle} from '@udemy/react-discovery-units';
import type {RelatedCategoryItem} from '@udemy/react-recommendations';

import {RelatedCategoryLinks} from 'src/components/related-category-links/related-category-links.react-component';

import {PRESET_ALL} from '../constants';
import styles from '../topic.module.less';
import {isOrgTopicPage, isSubsTopicPage, TopicPageType} from '../types/page';
import {HEADER_VARIANT, type HeaderVariant, type TopicContext} from '../types/topic';
import {TopicCertificationBanner} from './topic-certification-banner.react-component';
import {TopicHeaderSkeleton} from './topic-header-skeleton.react-component';

export const getHeaderVariant = ({
    certification,
    certDescription,
    tertiaryDescription,
    relatedCertificationBrowseAggregators,
    browseAggregatorPageEnabled,
    numCourses,
    numExercises,
    avgRating,
    topicName,
    isExperimentConfigsLoading,
}: Pick<
    TopicHeaderProps,
    | 'certification'
    | 'certDescription'
    | 'tertiaryDescription'
    | 'relatedCertificationBrowseAggregators'
    | 'browseAggregatorPageEnabled'
    | 'numCourses'
    | 'numExercises'
    | 'avgRating'
    | 'topicName'
    | 'isExperimentConfigsLoading'
>): HeaderVariant => {
    if (certDescription && isExperimentConfigsLoading) {
        return {type: HEADER_VARIANT.LOADING};
    }

    if (certification) {
        return {
            type: HEADER_VARIANT.CERTIFICATION,
            certification,
            relatedCertificationBrowseAggregators,
            browseAggregatorPageEnabled,
        };
    }
    if (tertiaryDescription) {
        return {
            type: HEADER_VARIANT.CONTEXTUAL,
            description: tertiaryDescription,
            numCourses,
            numExercises,
            avgRating,
        };
    }
    return {type: HEADER_VARIANT.DEFAULT, topicName};
};

interface TopicHeaderProps {
    certification?: CertificationModel;
    certDescription?: TopicContext['certDescription'];
    tertiaryDescription: TopicContext['tertiaryDescription'];
    relatedCertificationBrowseAggregators: TopicContext['relatedCertificationBrowseAggregators'];
    browseAggregatorPageEnabled?: boolean;
    numCourses: TopicContext['numCourses'];
    numExercises: TopicContext['numExercises'];
    avgRating: TopicContext['avgRating'];
    topicName: string;
    topicTitle: string;
    presetValue: string;
    pageType: TopicPageType;
    localizedEnrollmentStat?: string;
    relatedCategories?: RelatedCategoryItem[];
    isMobile: boolean;
    isLoading: boolean;
    isExperimentConfigsLoading?: boolean;
}

export const TopicHeader: React.FC<TopicHeaderProps> = ({
    certification,
    certDescription,
    tertiaryDescription,
    relatedCertificationBrowseAggregators,
    browseAggregatorPageEnabled,
    numCourses,
    numExercises,
    avgRating,
    topicName,
    topicTitle,
    presetValue,
    pageType,
    localizedEnrollmentStat,
    relatedCategories,
    isMobile,
    isLoading,
    isExperimentConfigsLoading = false,
}) => {
    const variant = getHeaderVariant({
        certification,
        certDescription,
        tertiaryDescription,
        relatedCertificationBrowseAggregators,
        browseAggregatorPageEnabled,
        numCourses,
        numExercises,
        avgRating,
        topicName,
        isExperimentConfigsLoading,
    });

    const primaryHeading = useMemo(() => {
        const showOnlyTopicName = isSubsTopicPage(pageType) || isOrgTopicPage(pageType);
        if (presetValue === PRESET_ALL && showOnlyTopicName) {
            return topicName;
        }
        return topicTitle;
    }, [topicName, topicTitle, presetValue, pageType]);

    switch (variant.type) {
        case HEADER_VARIANT.LOADING:
            return <TopicHeaderSkeleton />;
        case HEADER_VARIANT.CERTIFICATION:
            return (
                <TopicCertificationBanner
                    certification={variant.certification}
                    enrollmentStat={localizedEnrollmentStat}
                    relatedCategoriesAndSubcategories={relatedCategories}
                    className="ud-container"
                    isMobile={isMobile}
                    relatedCertificationBrowseAggregators={
                        variant.relatedCertificationBrowseAggregators
                    }
                    browseAggregatorPageEnabled={variant.browseAggregatorPageEnabled}
                />
            );
        case HEADER_VARIANT.CONTEXTUAL:
            return (
                <ContextualHeader
                    className="ud-container"
                    title={primaryHeading}
                    description={variant.description}
                    enrollmentStat={localizedEnrollmentStat ?? ''}
                    numCourses={variant.numCourses}
                    numExercises={variant.numExercises}
                    rating={variant.avgRating}
                    relatedCategoriesAndSubcategories={relatedCategories}
                />
            );
        case HEADER_VARIANT.DEFAULT:
        default:
            return (
                <div
                    className={classNames(
                        'ud-container',
                        'ud-page-wrapper',
                        styles['default-header'],
                    )}
                    data-purpose="topic-default-header"
                >
                    {isLoading ? (
                        /**
                         * The alternate headline in the loading state is for the seo purposes.
                         * We're only serving our pages to bots if we're in the mx and not logged in. But title is changing for below
                         * user-product types:
                         * - Logged out - MX
                         * - Logged in - Subs
                         * - Logged in - UFB
                         *
                         * But since we're using same page for those 3 pairs, we need to wait to load the title. But as you can see
                         * we only serve Logged out - MX pair to the bots so actually there's only one title that bots can see.
                         *
                         * That's why, while loading, we're just injecting a screen reader only title for cover the waiting for getting
                         * the subscription or ud me data in order to render correct title.
                         */
                        <>
                            <SkeletonTitle
                                className={styles['title-skeleton']}
                                data-item-index={0}
                            />
                            <AlternateHeadline
                                title={primaryHeading}
                                className="ud-sr-only"
                                titleTag="h1"
                            />
                        </>
                    ) : (
                        <>
                            <AlternateHeadline
                                title={primaryHeading}
                                titleTag="h1"
                                titleClass={
                                    isMobile ? 'ud-heading-serif-xl' : 'ud-heading-serif-xxl'
                                }
                                titleStyle={isMobile ? 'title-compact' : 'title'}
                                layoutVariant={isMobile ? 'default' : 'large'}
                            />
                            <RelatedCategoryLinks
                                className={
                                    localizedEnrollmentStat
                                        ? styles['related-categories-margin-sm']
                                        : styles['related-categories-margin-lg']
                                }
                                isMobile={isMobile}
                                relatedCategoriesAndSubcategories={relatedCategories}
                                topicTitle={variant.topicName}
                            />
                        </>
                    )}
                </div>
            );
    }
};
